import React, { useMemo, useState } from 'react';
import listingPageCss from '../ListingPage.module.css';
import { Heading, IconArrowHead } from '../../../components';
import css from './SectionClassSchedule.module.css';

import {
  addDays,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isToday,
  isTomorrow,
  isWithinInterval,
  startOfDay,
  subDays,
} from 'date-fns';

import { Alert, Text } from '@chakra-ui/react';
import classnames from 'classnames';
import { isEmpty, isNil } from 'lodash';
import { useGetClassScheduleByListingIdQuery } from '../../../api/store/apiSlice';

const DAYS_IN_VIEW = 7;
const MAX_DAYS_AHEAD = 14;

const SectionClassScheduleMaybe = ({ listingId }) => {
  const { start, end } = useMemo(() => {
    const start = new Date();
    start.setUTCHours(0, 0, 0, 0);

    const end = addDays(start, MAX_DAYS_AHEAD);

    return {
      start: start.toISOString(),
      end: end.toISOString(),
    };
  }, []);

  const { data = [] } = useGetClassScheduleByListingIdQuery(
    { listingId: listingId.uuid, start, end },
    {
      skip: isNil(listingId),
    }
  );

  const classSchedule = useMemo(() => {
    return data?.data?.data || [];
  }, [data]);

  if (isNil(classSchedule) || isEmpty(classSchedule)) {
    return null;
  }

  return (
    <section id="class-schedule" className={listingPageCss.sectionClassSchedule}>
      <Heading as="h2" rootClassName={listingPageCss.sectionHeadingWithExtraMargin}>
        Schedule
      </Heading>
      <WeeklyFitnessCalendar classSchedule={classSchedule} />
    </section>
  );
};

const WeeklyFitnessCalendar = ({ classSchedule }) => {
  const today = startOfDay(new Date());
  const [hasError, setHasError] = useState(false);

  const allTimeSlots = useMemo(() => {
    try {
      return classSchedule.map(slot => {
        const { start, end } = slot.attributes;

        const startDate = new Date(start).toISOString();
        const endDate = new Date(end).toISOString();

        return {
          id: slot.id?.uuid,
          start: startDate,
          end: endDate,
        };
      });
    } catch (error) {
      console.log(error);
      setHasError(true);

      return [];
    }
  }, [classSchedule]);

  // 🔹 Rolling window starts TODAY
  const [currentStart, setCurrentStart] = useState(today);

  const maxFutureStart = addDays(today, MAX_DAYS_AHEAD);
  const windowEnd = addDays(currentStart, DAYS_IN_VIEW);

  const disablePrev = isSameDay(currentStart, today) || isBefore(currentStart, today);
  const disableNext =
    isSameDay(addDays(currentStart, 7), maxFutureStart) ||
    isAfter(addDays(currentStart, 7), maxFutureStart);

  const nextWindow = () => {
    if (!disableNext) setCurrentStart(addDays(currentStart, DAYS_IN_VIEW));
  };

  const prevWindow = () => {
    if (!disablePrev) setCurrentStart(subDays(currentStart, DAYS_IN_VIEW));
  };

  const days = Array.from({ length: DAYS_IN_VIEW }).map((_, i) => addDays(currentStart, i));

  const slotsInWindow = allTimeSlots.filter(slot =>
    isWithinInterval(slot.start, {
      start: currentStart,
      end: windowEnd,
    })
  );

  const ErrorAlert = () => (
    <Alert.Root status="error">
      <Alert.Indicator />
      <Alert.Content>
        <Alert.Title fontWeight={'semibold'}>Failed to load class schedules</Alert.Title>
        <Alert.Description>
          Please refresh the page - if that doesn't work, contact us at hello@footfallhub.com.
        </Alert.Description>
      </Alert.Content>
    </Alert.Root>
  );

  return hasError ? (
    <ErrorAlert />
  ) : (
    <div className={css.weeklyCalendar}>
      <div className={css.calendarHeader}>
        <button
          className={classnames(css.navLeft, {
            [css.activeNav]: !disablePrev,
            [css.disabledNav]: disablePrev,
          })}
          onClick={prevWindow}
          disabled={disablePrev}
        >
          <IconArrowHead
            rootClassName={classnames({
              [css.disabledNavIcon]: disablePrev,
            })}
            direction="left"
            size="big"
          />
        </button>
        <Text color={'almostBlack'} fontWeight="bold" textStyle="md" textWrap={'nowrap'}>
          {format(currentStart, 'MMM d')} – {format(windowEnd, 'MMM d, yyyy')}
        </Text>
        <button
          className={classnames(css.navLeft, {
            [css.activeNav]: !disableNext,
            [css.disabledNav]: disableNext,
          })}
          onClick={nextWindow}
          disabled={disableNext}
        >
          <IconArrowHead direction="right" size="big" />
        </button>
      </div>
      <div className={css.calendarTableContainer}>
        <div className={css.calendarTable}>
          <table>
            <tbody>
              {days.map(day => {
                const slotsForDay = slotsInWindow.filter(slot => isSameDay(slot.start, day));
                return (
                  <tr key={day.toISOString()}>
                    <th className={css.dayHeader}>
                      {isToday(day) ? 'Today' : isTomorrow(day) ? 'Tomorrow' : format(day, 'EE do')}
                    </th>
                    {slotsForDay.length > 0 ? (
                      slotsForDay.map(slot => {
                        const classLengthInMinutes = Math.floor(
                          (new Date(slot.end).getTime() - new Date(slot.start).getTime()) /
                            1000 /
                            60
                        );
                        return (
                          <td key={slot.id} className={css.timeSlot}>
                            <Text
                              color={'almostBlack'}
                              fontWeight="semibold"
                              textStyle="sm"
                              textWrap={'nowrap'}
                              m={'0.5rem 0 0 0'}
                              p={0}
                            >
                              {format(slot.start, 'h:mm aa')}
                            </Text>
                            <Text
                              color={'lightGreyText'}
                              fontWeight="medium"
                              textStyle="sm"
                              textWrap={'nowrap'}
                              m={0}
                              p={0}
                            >
                              {classLengthInMinutes > 0 ? `${classLengthInMinutes} min` : ''}
                            </Text>
                          </td>
                        );
                      })
                    ) : (
                      <td className={css.timeSlot}>
                        <Text
                          color={'lightGreyText'}
                          fontWeight="medium"
                          textStyle="sm"
                          m={0}
                          p={0}
                          textAlign={'center'}
                          align={'center'}
                          justify={'center'}
                        >
                          -
                        </Text>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SectionClassScheduleMaybe;
